import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { Login } from './components/Login';
import { Dashboard } from './components/Dashboard';
import { Events } from './components/Events';
import { Navbar } from './components/Navbar';
import { api } from './services/api';
import notificationManager from './utils/notificationManager';

const App = () => {
  const [authStatus, setAuthStatus] = useState(
    !!localStorage.getItem('token')
  );
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [serverStatus, setServerStatus] = useState(null);
  const previousEventCount = useRef(0);

  const fetchData = useCallback(async () => {
    if (!authStatus) return;
  
    try {
      setError(null);
      const [eventsData, statusData] = await Promise.all([
        api.getEvents(),
        api.getHeartbeat()
      ]);
      
      // Check for new events and trigger notifications
      if (eventsData && eventsData.length > previousEventCount.current) {
        const newEventCount = eventsData.length - previousEventCount.current;
        
        console.log('=== EVENT COUNT CHECK ===');
        console.log('Previous count:', previousEventCount.current);
        console.log('New count:', eventsData.length);
        console.log('New events:', newEventCount);
        
        // Only notify if this isn't the initial load (previousEventCount > 0)
        if (previousEventCount.current > 0) {
          console.log('🔔 TRIGGERING NOTIFICATION for', newEventCount, 'new event(s)');
          await notificationManager.notifyNewEvents(newEventCount);
        } else {
          console.log('⚠️ Skipping notification (initial load)');
        }
      } else {
        console.log('No new events. Current:', eventsData?.length, 'Previous:', previousEventCount.current);
      }
      
      previousEventCount.current = eventsData ? eventsData.length : 0;
      setEvents(eventsData);
      setServerStatus(statusData);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, [authStatus]); 

  // Check server heartbeat more frequently than events
  useEffect(() => {
    if (authStatus) {
      const checkHeartbeat = async () => {
        try {
          const status = await api.getHeartbeat();
          setServerStatus(status);
        } catch (error) {
          console.error('Heartbeat check failed:', error);
        }
      };

      // Check immediately
      checkHeartbeat();
      
      // Then check every 15 seconds
      const heartbeatInterval = setInterval(checkHeartbeat, 5000);
      return () => clearInterval(heartbeatInterval);
    }
  }, [authStatus]);

  // Fetch events data
  useEffect(() => {
    if (authStatus) {
      setLoading(true);
      fetchData();
      
      let interval;
      if (!selectedEventId) {
        interval = setInterval(fetchData, 5000);
      }

      return () => {
        if (interval) {
          clearInterval(interval);
        }
      };
    }
  }, [authStatus, selectedEventId, fetchData]);

  return (
    <Router>
      <div className="min-h-screen bg-gray-100">
        <Navbar 
          isAuthenticated={authStatus} 
          setIsAuthenticated={setAuthStatus} 
        />
        
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mx-4 mt-4">
            {error}
          </div>
        )}

        <Routes>
          <Route 
            path="/login" 
            element={
              authStatus ? (
                <Navigate to="/events" />
              ) : (
                <Login setIsAuthenticated={setAuthStatus} />
              )
            } 
          />
          <Route 
            path="/dashboard" 
            element={
              <PrivateRoute isAuthenticated={authStatus}>
                <Dashboard 
                  events={events} 
                  loading={loading}
                  serverStatus={serverStatus} 
                />
              </PrivateRoute>
            } 
          />
          <Route 
            path="/events" 
            element={
              <PrivateRoute isAuthenticated={authStatus}>
                <Events 
                  events={events}
                  loading={loading}
                  onEventsUpdate={fetchData}
                  onEventSelect={setSelectedEventId}
                  selectedEventId={selectedEventId}
                  serverStatus={serverStatus}
                />
              </PrivateRoute>
            } 
          />
          <Route 
            path="/" 
            element={<Navigate to="/dashboard" />} 
          />
        </Routes>
      </div>
    </Router>
  );
};

const PrivateRoute = ({ children, isAuthenticated }) => {
  return isAuthenticated ? children : <Navigate to="/login" />;
};

export default App;